import styled from '@emotion/styled';
import { Colors } from '../Colors';
import { Variables } from '../Variables';
import { helperTypographyText, helperSafeArea, helperAbsoluteFill } from '../../helpers';
import { media } from '../../styles/MediaQueries';
import { exceptStyledProps, getRem, setDeclaration } from '../../utils';
import { ArrayUnitValue, Media } from '../../types';

interface ContainerProps {
  duration: number;
  isOpen: boolean;
}

export const Container = styled('div')<ContainerProps>`
  position: fixed;
  z-index: 900;

  @media ${media.phonablet} {
    z-index: 2000;
    overflow: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
    padding-top: 3rem;
  }

  @media ${media.phonablet} {
    &:before {
      ${helperAbsoluteFill};
      content: '';
      z-index: -1;
    }

    &.tooltip-enter-active,
    &.tooltip-enter-done,
    &.tooltip-exit {
      &:before {
        transition: background ${props => `${props.duration}ms`};
      }
    }

    &.tooltip-enter-active,
    &.tooltip-enter-done {
      &:before {
        background-color: var(--tooltip-overlay-bg, rgba(4, 4, 15, 0.4));
      }
    }
  }
`;

interface WrapperProps {
  duration: number;
  width: number | 'auto';
  isOpen: boolean;
}

export const Wrapper = styled('div', exceptStyledProps('width'))<WrapperProps>`
  flex-shrink: 0;
  width: ${props => getRem(props.width)};
  max-width: ${props => props.width === 'auto' && '32rem'};
  text-align: left;
  border-radius: ${Variables.tooltipRadius};
  box-shadow: ${Variables.tooltipShadow};
  opacity: 0;

  [data-popper-placement^='left'] & {
    transform: translateX(-1rem);
  }

  [data-popper-placement^='right'] & {
    transform: translateX(1rem);
  }

  [data-popper-placement^='bottom'] & {
    transform: translateY(1rem);
  }

  [data-popper-placement^='top'] & {
    transform: translateY(-1rem);
  }

  .tooltip-enter-active &,
  .tooltip-enter-done &,
  .tooltip-exit & {
    transition-duration: ${props => `${props.duration}ms`};
    transition-property: transform, opacity;
  }

  .tooltip-enter-active &,
  .tooltip-enter-done & {
    opacity: 1;
    transform: translate(0, 0) !important;
  }

  @media ${media.phonablet} {
    overflow: hidden;
    width: auto;
    max-width: none;
    margin-top: auto;
    border: 0;
    border-radius: 2rem 2rem 0 0;

    [data-popper-placement] & {
      transform: translateY(100%);
    }
  }
`;

interface InnerProps {
  padding: ArrayUnitValue | Media<ArrayUnitValue>;
}

export const Inner = styled('div')<InnerProps>`
  position: relative;
  z-index: 2;
  color: ${Colors.text.default};
  background-color: ${Colors.ut.bgUp};
  border-radius: inherit;
  transform: translateZ(0);
  ${props => setDeclaration('padding', props.padding, 'rem')};
  ${helperTypographyText({
    size: 'normal'
  })};

  @media ${media.phonablet} {
    ${helperSafeArea};
  }
`;

export const ButtonWrapper = styled('div')`
  position: absolute;
  top: 1.8rem;
  right: 1.8rem;

  @media ${media.tablesktop} {
    top: 1rem;
    right: 1rem;
  }
`;

interface ArrowProps {
  isVisible?: boolean;
}

export const Arrow = styled('div')<ArrowProps>`
  visibility: ${props => !props.isVisible && 'hidden'};

  &,
  &:before {
    position: absolute;
    z-index: 1;
    width: 1.9rem;
    height: 1.9rem;
  }

  &:before {
    content: '';
    left: 0;
    top: 0;
    background-color: ${Colors.ut.bgUp};
    box-shadow: 0 0 0 1px ${Colors.ut.shadow10};
    transform: rotate(45deg);
  }

  [data-popper-placement^='left'] & {
    right: -0.7rem;
  }

  [data-popper-placement^='right'] & {
    left: -0.7rem;
  }

  [data-popper-placement^='top'] & {
    bottom: -0.7rem;
  }

  [data-popper-placement^='bottom'] & {
    top: -0.7rem;
  }

  @media ${media.phonablet} {
    visibility: hidden;
  }
`;
